import React from 'react';
import { TickIcon } from '../icons';
import { formatMoney, formatNumber } from '../format';

// Kore's answer for a row, as the row says it. Review is drawn from the answer
// someone gave, so it has no fixed label.
const KORE_CHIPS = {
    held: { tone: 'held', label: 'In iCLIMB' },
    clear: { tone: 'clear', label: 'New owner' },
    found: { tone: 'found', label: 'In Kore' },
    checking: { tone: 'waiting', label: 'Checking…' },
    failed: { tone: 'failed', label: 'Kore error' },
    unchecked: { tone: 'waiting', label: 'Not checked' },
};

// Approving adds these without anyone answering first.
const SETTLED = ['held', 'clear', 'found', 'none'];

const TABS = [
    { key: 'all', label: 'All', count: 'all' },
    { key: 'review', label: 'Needs review', count: 'review' },
    { key: 'checking', label: 'Checking', count: 'checking' },
];

// Only an empty All tab means every row was decided; the others are slices of it.
const EMPTY_TABS = {
    review: 'No rows need review.',
    checking: 'No rows are being checked.',
};

function koreStatus(row) {
    return (row.kore && row.kore.status) || 'unchecked';
}

/** Rows are grouped by who the roll names, so one answer covers every parcel an owner has. */
export function ownerKey(row) {
    const parcel = row.parcel || {};

    return [parcel.owner_name, parcel.owner_address].map((part) => String(part || '').trim().toUpperCase()).join('|');
}

/** Whether approving adds the row now: Kore cleared it, or someone answered for its owner. */
export function isReadyToAdd(row, answers) {
    if (row.matched_property_id) return true;

    const status = koreStatus(row);

    return SETTLED.indexOf(status) !== -1 || (status === 'review' && Boolean(answers[ownerKey(row)]));
}

/** The owners Kore was unsure about among these rows, each with every parcel of theirs. */
export function reviewGroups(rows) {
    const groups = [];
    const byKey = {};

    rows.forEach((row) => {
        if (koreStatus(row) !== 'review') return;

        const key = ownerKey(row);

        if (!byKey[key]) {
            byKey[key] = { key, rows: [], parcel: row.parcel || {}, kore: row.kore };
            groups.push(byKey[key]);
        }

        byKey[key].rows.push(row);
    });

    return groups;
}

/** Why the ticked rows that are not ready stay, in the order they get resolved. */
function waitingNote(rows, answers) {
    const count = { review: 0, checking: 0, unanswered: 0 };

    rows.forEach((row) => {
        if (isReadyToAdd(row, answers)) return;

        const status = koreStatus(row);

        if (status === 'review') count.review++;
        else if (status === 'checking') count.checking++;
        else count.unanswered++;
    });

    const parts = [
        count.review && count.review + ' need review',
        count.checking && count.checking + ' still being checked',
        count.unanswered && count.unanswered + ' not checked by Kore',
    ].filter(Boolean);

    return parts.length ? parts.join(', ') + ' — they stay in the queue.' : null;
}

function ReviewChip({ row, answer, onReview }) {
    const open = (event) => {
        event.stopPropagation();
        onReview(ownerKey(row));
    };

    if (answer) {
        return (
            <button type="button" className="Queue-kore Queue-kore--answered" onClick={open}>
                {answer.createNew ? 'New owner · chosen' : 'Use: ' + (answer.leadName || 'Kore lead ' + answer.koreLeadId)}
            </button>
        );
    }

    return (
        <button type="button" className="Queue-kore Queue-kore--review" onClick={open}>
            Review · {row.kore.total} possible
        </button>
    );
}

function OwnerLine({ row, answer, onReview }) {
    const parcel = row.parcel || {};
    const status = koreStatus(row);

    if (row.matched_property_id) return null;

    if (!parcel.owner_name) {
        return <div className="Queue-owner Queue-owner--none">No owner on record</div>;
    }

    const chip = KORE_CHIPS[status];

    return (
        <div className="Queue-owner">
            <span className="Queue-owner-name">Owner: {parcel.owner_name}</span>
            {status === 'review' && <ReviewChip row={row} answer={answer} onReview={onReview} />}
            {chip && <span className={'Queue-kore Queue-kore--' + chip.tone}>{chip.label}</span>}
        </div>
    );
}

/**
 * The manual approval queue for a ZIP import.
 *
 * Nothing an import fetched reaches PROPERTIES until it is ticked here, so the
 * default is everything selected and the work is unticking what you do not
 * want.
 *
 * The whole queue is reachable by scrolling. It used to arrive capped at 500
 * with a line of help text explaining that the rest could only be decided by
 * clearing those first — on an import holding 3,410 rows that is seven rounds
 * of a job the user only meant to do once.
 */
export default class QueueStep extends React.Component {
    constructor(props) {
        super(props);

        this.sentinel = null;
        this.observer = null;
    }

    componentDidMount() {
        this.watchSentinel();
    }

    componentDidUpdate() {
        this.watchSentinel();
    }

    componentWillUnmount() {
        if (this.observer) this.observer.disconnect();
    }

    /**
     * Fetch the next page when the end of the list comes into view.
     *
     * Observed against the scrolling list rather than the window: the queue
     * sits in a side panel that scrolls on its own, so the sentinel is never
     * near the viewport edge and a window-relative check would fire once at
     * mount and then never again.
     */
    watchSentinel() {
        if (this.observer) this.observer.disconnect();

        if (!this.sentinel || !this.list || !this.props.hasMore) return;

        if (typeof IntersectionObserver === 'undefined') return;

        this.observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) this.props.onLoadMore();
            },
            // Start the fetch a screenful early so the rows are usually there
            // before the user reaches the bottom.
            { root: this.list, rootMargin: '400px' }
        );

        this.observer.observe(this.sentinel);
    }

    renderTabs() {
        const { tab, counts, onTab } = this.props;

        if (!counts) return null;

        return (
            <div className="Sel-tabs" role="tablist">
                {TABS.map((each) => (
                    <button
                        type="button"
                        role="tab"
                        key={each.key}
                        aria-selected={tab === each.key}
                        className={'Sel-tab' + (tab === each.key ? ' Sel-tab--on' : '')
                            + (each.key === 'review' && counts.review > 0 ? ' Sel-tab--warn' : '')}
                        onClick={() => onTab(each.key)}
                    >
                        {each.label} {(counts[each.count] || 0).toLocaleString()}
                    </button>
                ))}
            </div>
        );
    }

    render() {
        const {
            rows, total, hasMore, isLoading, isLoadingMore, rejected, isSaving, counts, answers, tab,
            onToggle, onToggleAll, onApprove, onReject, onReview, onRecheck,
        } = this.props;

        if (isLoading) return <div className="SidePanel-loading">Loading queue…</div>;

        if (rows.length === 0) {
            return (
                <div className="SidePanel-step">
                    {this.renderTabs()}
                    <div className="SidePanel-empty">
                        {EMPTY_TABS[tab] || <React.Fragment>Nothing left to review.<br />Every row has been decided.</React.Fragment>}
                    </div>
                </div>
            );
        }

        const showKoreActions = counts && (counts.unanswered > 0 || counts.review > 0);

        const approved = rows.filter((row) => !rejected[row.id]);
        const ready = approved.filter((row) => isReadyToAdd(row, answers));
        const note = waitingNote(approved, answers);
        // Counted against the queue's real size, not the page on screen. The
        // page count alone read as "500 of 500 approved" on an import holding
        // thousands, which looks like the job is finished.
        const loadedAll = !hasMore;

        return (
            <div className="SidePanel-step">
                {this.renderTabs()}

                <div className="Sel-bulkbar Sel-bulkbar--queue">
                    <button type="button" className="Btn Btn--sm" onClick={onToggleAll}>
                        {approved.length === rows.length ? 'Select none' : 'Select all'}
                    </button>
                    <div className="ParcelMap-spacer" />
                    <div className="Sel-count">
                        {ready.length.toLocaleString()} of {(total || rows.length).toLocaleString()} ready to add
                    </div>
                    {showKoreActions && (
                        <div className="Queue-kore-actions">
                            {counts.unanswered > 0 && (
                                <button type="button" className="Btn Btn--sm" onClick={onRecheck} disabled={isSaving}>
                                    Check {counts.unanswered.toLocaleString()} in Kore
                                </button>
                            )}
                            {counts.review > 0 && (
                                <button type="button" className="Btn Btn--sm Queue-review" onClick={() => onReview(null)}>
                                    Review {counts.review.toLocaleString()}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                <div className="Sel-list" ref={(node) => { this.list = node; }}>
                    {rows.map((row) => {
                        const on = !rejected[row.id];
                        const parcel = row.parcel || {};
                        const isUpdate = Boolean(row.matched_property_id);

                        return (
                            <div
                                key={row.id}
                                className={'Sel-row' + (on ? '' : ' Sel-row--off')}
                                onClick={() => onToggle(row.id)}
                            >
                                <div className={'Check' + (on ? ' Check--on' : '')}><TickIcon /></div>
                                <div className="Sel-row-body">
                                    <div className="Sel-row-addr">{row.address || parcel.address || row.apn}</div>
                                    <div className="Sel-row-sub">
                                        {[parcel.city, formatNumber(parcel.building_sqft) && formatNumber(parcel.building_sqft) + ' SF', formatMoney(parcel.assessed_value)]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </div>
                                    <OwnerLine row={row} answer={answers[ownerKey(row)]} onReview={onReview} />
                                </div>
                                <div className={'Queue-pill Queue-pill--' + (isUpdate ? 'update' : 'new')}>
                                    {isUpdate ? 'Update' : 'New'}
                                </div>
                            </div>
                        );
                    })}

                    {/* Kept in the DOM whenever there is more to fetch, because
                        the observer needs something to watch that the last row
                        will push into view. */}
                    {hasMore && (
                        <div className="Queue-more" ref={(node) => { this.sentinel = node; }}>
                            {isLoadingMore
                                ? 'Loading more…'
                                : (total - rows.length).toLocaleString() + ' more below'}
                        </div>
                    )}
                </div>

                <div className="SidePanel-footer Queue-footer">
                    <div className="Queue-actions">
                        <button
                            type="button"
                            className="Btn Btn--grow Btn--primary"
                            onClick={onApprove}
                            disabled={isSaving || ready.length === 0}
                        >
                            {isSaving ? 'Submitting…' : 'Approve ' + ready.length.toLocaleString()}
                        </button>
                        <button
                            type="button"
                            className="Btn Btn--danger"
                            onClick={onReject}
                            disabled={isSaving || approved.length === rows.length}
                        >
                            {loadedAll ? 'Reject rest' : 'Reject unticked'}
                        </button>
                    </div>
                    {note && <div className="Queue-note">{note}</div>}
                </div>
            </div>
        );
    }
}
